import React from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { MainRouteKeys } from 'routes/enums';
import { MainRoutesConfig } from 'routes/main/config';
import { useCurrentPath } from 'utils/hooks';
import { menusRenderer } from 'configs/menu';

type BreadcrumbItem = {
  pathname: string;
  route: {
    path?: string;
  };
};

const Breadcrumbs = (): JSX.Element => {
  const { paths } = useCurrentPath();

  const getRouteOptions = (pathname: string, routePath?: string) => {
    return (
      MainRoutesConfig?.[pathname as MainRouteKeys] ||
      MainRoutesConfig?.[routePath as MainRouteKeys]
    );
  };

  const getBreadcrumbPaths = (): BreadcrumbItem[] => {
    const breadcrumbPaths = [...paths] as BreadcrumbItem[];
    const currentPath = breadcrumbPaths[breadcrumbPaths.length - 1]?.pathname;
    const parentMenu = menusRenderer().find((menu) =>
      menu.childs?.some((child) => child.path === currentPath),
    );

    if (
      !parentMenu ||
      breadcrumbPaths.some((item) => item.pathname === parentMenu.path)
    ) {
      return breadcrumbPaths;
    }

    return [
      ...breadcrumbPaths.slice(0, -1),
      {
        pathname: parentMenu.path,
        route: {
          path: parentMenu.path,
        },
      },
      ...breadcrumbPaths.slice(-1),
    ];
  };

  const breadcrumbPaths = getBreadcrumbPaths();

  return (
    <nav aria-label='breadcrumb' className='Breadcrumbs'>
      <ul>
        {breadcrumbPaths.map((item, index) => {
          const routeOptions = getRouteOptions(
            item.pathname,
            String(item.route.path || ''),
          );
          const label = routeOptions?.name || item.pathname;
          return (
            <li key={item.pathname} className='breadcrumb-item'>
              {index === breadcrumbPaths.length - 1 ? (
                <span className='breadcrumb-item active' aria-current='page'>
                  <BreadcrumbDetails getRouteOptions={label} />
                </span>
              ) : (
                <Link to={item.pathname}>{label}</Link>
              )}
              {index < breadcrumbPaths.length - 1 && (
                <span className='separator'>{'>'}</span>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

function BreadcrumbDetails({ getRouteOptions }: { getRouteOptions: string }) {
  const { orderCode, transactionCode } = useParams();
  const location = useLocation();

  if (orderCode) {
    return (
      <>
        <Link to={MainRoutesConfig[MainRouteKeys.PayBillElectric].path}>
          {MainRoutesConfig[MainRouteKeys.PayBillElectric].name}
        </Link>
        <span className='separator'>{'>'}</span>
        {orderCode}
      </>
    );
  }

  if (transactionCode) {
    if (location.pathname.startsWith('/transactions/points/')) {
      return (
        <>
          <Link to={MainRoutesConfig[MainRouteKeys.PointTransactions].path}>
            {MainRoutesConfig[MainRouteKeys.PointTransactions].name}
          </Link>
          <span className='separator'>{'>'}</span>
          {transactionCode}
        </>
      );
    }
    return (
      <>
        <Link to={MainRoutesConfig[MainRouteKeys.TransactionBills].path}>
          {MainRoutesConfig[MainRouteKeys.TransactionBills].name}
        </Link>
        <span className='separator'>{'>'}</span>
        {transactionCode}
      </>
    );
  }

  return <span>{getRouteOptions}</span>;
}

export default Breadcrumbs;
